import React, { useMemo } from 'react';
import { useJobStatus } from '../contexts/JobStatusContext.jsx';

const stateStyles = {
  queued: { badge: 'bg-gray-100 text-gray-700', progress: 'bg-gray-400', label: 'En attente' },
  started: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'En cours' },
  uploading_user_file: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'En cours' },
  reading_excel: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Lecture' },
  creating_mapping: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Création du mapping' },
  mapping_sheet: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Mapping' },
  uploading_to_storage: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Upload Azure' },
  ingesting_into_snowflake: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Snowflake' },
  updating_dwh: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Mise à jour DWH' },
  updating_dtm: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Mise à jour DTM' },
  uploading_output_excel: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Upload résultat' },
  fetching_history: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Récupération...' },
  downloading_excel: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Téléchargement Excel' },
  parsing_excel: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Analyse Excel' },
  soft_deleting_history: { badge: 'bg-orange-100 text-orange-700', progress: 'bg-orange-500', label: 'Nettoyage historique' },
  deleting_blobs: { badge: 'bg-orange-100 text-orange-700', progress: 'bg-orange-500', label: 'Suppression blobs' },
  no_blobs: { badge: 'bg-gray-100 text-gray-700', progress: 'bg-gray-400', label: 'Aucun blob' },
  deleting_snowflake: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Nettoyage Snowflake' },
  updating_db: { badge: 'bg-blue-100 text-blue-700', progress: 'bg-blue-500', label: 'Mise à jour DB' },
  success: { badge: 'bg-green-100 text-green-700', progress: 'bg-green-500', label: 'Terminé' },
  succeeded: { badge: 'bg-green-100 text-green-700', progress: 'bg-green-500', label: 'Terminé' },
  failed: { badge: 'bg-red-100 text-red-700', progress: 'bg-red-500', label: 'Échec' }
};

const formatState = (state) => {
  if (!state) {
    return 'Inconnu';
  }
  return stateStyles[state]?.label || state.replace(/_/g, ' ');
};

const getStyle = (state) => stateStyles[state] || { badge: 'bg-gray-100 text-gray-700', progress: 'bg-gray-400', label: state };

const parsePayload = (payload) => {
  if (!payload) {
    return {};
  }
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return payload ;
    }
  }
  return payload;
};

const formatDate = (value) => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleString();
};
const hasValidFileUrl = (payload,finishedAt) => {
  const ttl = payload?.ttl; // in minutes
  console.log(payload)
  if (!ttl) {
    return false;
  }
  const now = new Date();
  const diff = now.getTime() - finishedAt.getTime(); // now - finished
  return diff < ttl * 60 * 1000; // check if within TTL
}

function JobStatusDrawer() {
  const {
    jobOrder,
    jobs,
    isDrawerOpen,
    toggleDrawer,
    isLoading,
    isPolling,
    lastUpdated,
    lastError,
    refreshJobs
  } = useJobStatus();

  const jobList = useMemo(
    () => jobOrder.map((jobId) => jobs[jobId]).filter(Boolean),
    [jobOrder, jobs]
  );

  return (
    <div
      className={`fixed inset-0 z-40 flex justify-end transition-opacity duration-300 ${isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      style={{ opacity: isDrawerOpen ? 1 : 0, pointerEvents: isDrawerOpen ? 'auto' : 'none' }}
    >
      <div
        className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${isDrawerOpen ? 'opacity-100' : 'opacity-0'}`}
        style={{ opacity: isDrawerOpen ? 1 : 0 }}
        onClick={toggleDrawer}
        aria-hidden="true"
      ></div>

      <aside
        className={`relative z-50 h-full w-full max-w-md bg-white dark:bg-gray-900 shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ transform: isDrawerOpen ? 'translateX(0)' : 'translateX(100%)' }}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Suivi des Jobs</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isPolling ? 'Actualisation automatique toutes les 5 secondes' : 'Actualisation en pause'}
              {lastUpdated && ` · Dernière mise à jour : ${formatDate(lastUpdated)}`}
            </p>
          </div>
          <button
            onClick={toggleDrawer}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            aria-label="Fermer"
          >
            <span aria-hidden="true" className="text-xl text-gray-500 dark:text-gray-300">&times;</span>
          </button>
        </header>

        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300">
          <button
            onClick={() => refreshJobs()}
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            disabled={isLoading}
          >
            {isLoading ? 'Rafraîchissement...' : 'Rafraîchir'}
          </button>
          <span>
            {jobList.length === 0 ? 'Aucun job lancé pour cette session.' : `${jobList.length} job(s) suivi(s)`}
          </span>
        </div>

        {lastError && (
          <div className="mx-6 mt-4 rounded-md border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">
            Erreur lors de la récupération du statut : {lastError}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {jobList.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-6 text-center text-gray-500 dark:text-gray-400">
              Lancez un traitement pour suivre son avancement ici.
            </div>
          )}

          {jobList.map((job) => {
            const style = getStyle(job.state);
            const payload = parsePayload(job.payload);
            const shortId = job.id?.slice(0, 8) || job.id;
            const progressValue = typeof job.progress === 'number' ? Math.max(0, Math.min(job.progress, 100)) : 0;
            const finishedAt = new Date(job.finished_at);
            return (
              <article key={job.id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-400 dark:text-gray-500">Job #{shortId}</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {payload?.excel_name || 'Traitement Sellout'}
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-3 py-1 rounded-full ${style.badge}`}>
                    {formatState(job.state)}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
                  {payload?.client && <div><span className="font-medium">Client :</span> {payload.client}</div>}
                  {payload?.shop_sign && <div><span className="font-medium">Enseigne :</span> {payload.shop_sign}</div>}
                  {payload?.year && <div><span className="font-medium">Année :</span> {payload.year}</div>}
                  {payload?.week && <div><span className="font-medium">Semaine :</span> {payload.week}</div>}
                  {payload?.category && <div><span className="font-medium">Catégorie :</span> {payload.category}</div>}
                  {formatDate(job.created_at) && <div><span className="font-medium">Créé :</span> {formatDate(job.created_at)}</div>}
                  {formatDate(job.finished_at) && <div><span className="font-medium">Fin :</span> {formatDate(job.finished_at)}</div>}
                </div>

                <div className="mt-4">
                  <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <div className={`h-full transition-all duration-500 ${style.progress}`} style={{ width: `${progressValue}%` }}></div>
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Avancement</span>
                    <span>{progressValue}%</span>
                  </div>
                  {typeof payload === 'string' && <div className="mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400 wrap-break-word">
                    <span>{payload}</span>
                  </div>}
                </div>

                {job.error && (
                  <div className="mt-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 wrap-break-word">
                    {job.error}
                  </div>
                )}

                {['success', 'succeeded'].includes(job.state) && payload?.output_excel && hasValidFileUrl(payload,finishedAt) && (
                  <div className="mt-3">
                    <a
                      href={payload.output_excel}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Télécharger le fichier généré
                    </a>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </aside>
    </div>
  );
}

export default JobStatusDrawer;
